import { Citation, CitationData, CitationMetadata, CitationDescription, SourceInfo } from '../types/citation';
import { loadCitationMetadata, findColumnMetadata } from './citationMetadata';

/**
 * Parses content containing <CITE>[1]</CITE> tags and extracts citations
 * Supports both <CITE>[1][2]</CITE> (multiple IDs in one tag) and <CITE>[1]</CITE><CITE>[2]</CITE> (separate tags)
 * @param content - The content string containing <CITE>[number]</CITE> tags
 * @param citationDescriptions - Dictionary mapping citation IDs to descriptions from backend
 * @returns Object with processed content and extracted citations
 */
export async function parseCitations(
  content: string, 
  citationDescriptions?: { [citationId: number]: string }
): Promise<CitationData> {
  const citations: Citation[] = [];
  const citationRegex = /<CITE>((?:\[\d+\])+)<\/CITE>/g;
  const idRegex = /\[(\d+)\]/g;
  let match;

  // Load metadata once
  const metadataFile = await loadCitationMetadata();

  // Extract all citations
  while ((match = citationRegex.exec(content)) !== null) {
    const citationBlock = match[1]; // e.g., "[1][2]" or "[1]"
    let idMatch;
    
    // Extract all individual IDs from the citation block
    while ((idMatch = idRegex.exec(citationBlock)) !== null) {
      const citationId = parseInt(idMatch[1], 10);
      const citationDescription = citationDescriptions?.[citationId] || '';
      
      // Try to extract metadata information from the description if available
      let metadataInfo: CitationMetadata | undefined;
      
      if (metadataFile && citationDescription) {
        // Try to find relevant metadata based on description content
        // This is a simple approach - in practice, the backend should provide more structured mapping
        const columnMatch = citationDescription.match(/\b([a-z_]+)\b/g);
        if (columnMatch) {
          for (const potentialColumn of columnMatch) {
            const found = findColumnMetadata(metadataFile, potentialColumn);
            if (found) {
              metadataInfo = found.metadata;
              break;
            }
          }
        }
      }

      const shortPreview = citationDescription.length > 150 
        ? citationDescription.substring(0, 150) + '...'
        : citationDescription;

      citations.push({
        id: citationId,
        content: citationDescription,
        shortPreview,
        metadataInfo,
        detailedDescription: citationDescription
      });
    }
  }

  // Replace <CITE>[number]</CITE> and <CITE>[number][number]</CITE> tags with placeholder markers
  let processedContent = content;
  processedContent = processedContent.replace(citationRegex, (match, citationBlock) => {
    const ids: number[] = [];
    let idMatch;
    const localIdRegex = /\[(\d+)\]/g;
    
    while ((idMatch = localIdRegex.exec(citationBlock)) !== null) {
      ids.push(parseInt(idMatch[1], 10));
    }
    
    // Create placeholders for all IDs found in this CITE tag
    return ids.map(id => `__CITATION_${id}__`).join('');
  });

  return {
    citations,
    processedContent
  };
}

/**
 * Extracts source information for specific columns from citation metadata
 * @param columnsUsed - Array of column names used in the citation
 * @param citationMetadata - Raw citation metadata from backend
 * @returns Array of source information for each column
 */
function extractSourceInfo(columnsUsed: string[], citationMetadata?: { [key: string]: any }): SourceInfo[] {
  if (!columnsUsed || !citationMetadata?.tables) {
    return [];
  }

  const sourceInfo: SourceInfo[] = [];
  
  // Search through all tables to find column metadata
  for (const [tableName, tableData] of Object.entries(citationMetadata.tables)) {
    if (tableData && typeof tableData === 'object' && 'columns' in tableData) {
      const tableColumns = (tableData as any).columns;
      if (tableColumns && typeof tableColumns === 'object') {
        for (const columnName of columnsUsed) {
          const columnMetadata = tableColumns[columnName];
          if (columnMetadata && typeof columnMetadata === 'object') {
            sourceInfo.push({
              columnName,
              // Use the actual source_table and source_field from metadata
              sourceTable: columnMetadata.source_table || tableName,
              sourceField: columnMetadata.source_field || columnName,
              primarySources: columnMetadata.primary_sources || [],
              description: columnMetadata.description || '',
              base_transformation: columnMetadata.base_transformation || '',
              business_logic: columnMetadata.business_logic || ''
            });
          }
        }
      }
    }
  }

  return sourceInfo;
}

/**
 * Synchronous version for cases where we already have the citations parsed
 * Supports both <CITE>[1][2]</CITE> (multiple IDs in one tag) and <CITE>[1]</CITE><CITE>[2]</CITE> (separate tags)
 * @param content - The content string containing <CITE>[number]</CITE> tags
 * @param citationDescriptions - Dictionary mapping citation IDs to descriptions from backend (supports both old string format and new object format)
 * @param citationMetadata - Raw citation metadata from backend containing table/column info
 * @returns Object with processed content and extracted citations
 */
export function parseCitationsSync(
  content: string, 
  citationDescriptions?: { [citationId: number]: string | CitationDescription },
  citationMetadata?: { [key: string]: any }
): CitationData {
  const citations: Citation[] = [];
  const citationRegex = /<CITE>((?:\[\d+\])+)<\/CITE>/g;
  const idRegex = /\[(\d+)\]/g;
  let match;

  // Extract all citations
  while ((match = citationRegex.exec(content)) !== null) {
    const citationBlock = match[1]; // e.g., "[1][2]" or "[1]"
    let idMatch;
    
    // Extract all individual IDs from the citation block
    while ((idMatch = idRegex.exec(citationBlock)) !== null) {
      const citationId = parseInt(idMatch[1], 10);
      const citationData = citationDescriptions?.[citationId];
      
      // Handle both old string format and new object format
      let citationDescription: string;
      let columnsUsed: string[] = [];
      let citationTitle: string | undefined;
      
      if (typeof citationData === 'string') {
        // Old format - just a description string
        citationDescription = citationData;
      } else if (citationData && typeof citationData === 'object') {
        // New format - object with description, columns_used, and title
        citationDescription = citationData.description || '';
        columnsUsed = citationData.columns_used || [];
        citationTitle = citationData.title;
      } else {
        citationDescription = '';
      }

      const shortPreview = citationDescription.length > 150 
        ? citationDescription.substring(0, 150) + '...'
        : citationDescription;

      // Extract source information for columns used
      const sourceInfo = extractSourceInfo(columnsUsed, citationMetadata);

      // Try to extract relevant metadata for this citation
      let metadataInfo: CitationMetadata | undefined;
      
      if (citationMetadata && citationMetadata.tables) {
        // For now, we'll use the first table's metadata as a fallback
        // In a more sophisticated implementation, we'd map specific citations to specific tables/columns
        const tableNames = Object.keys(citationMetadata.tables);
        if (tableNames.length > 0) {
          const firstTable = citationMetadata.tables[tableNames[0]];
          metadataInfo = {
            description: firstTable.description,
            join_logic: firstTable.join_logic,
            source_table: tableNames[0],
            // Add column information if available
            ...firstTable.columns && Object.keys(firstTable.columns).length > 0 
              ? { columns: Object.keys(firstTable.columns) }
              : {}
          };
        }
      }

      citations.push({
        id: citationId,
        content: citationDescription,
        shortPreview,
        title: citationTitle,
        metadataInfo,
        detailedDescription: citationDescription,
        columnsUsed,
        sourceInfo
      });
    }
  }

  // Replace <CITE>[number]</CITE> and <CITE>[number][number]</CITE> tags with placeholder markers
  let processedContent = content;
  processedContent = processedContent.replace(citationRegex, (match, citationBlock) => {
    const ids: number[] = [];
    let idMatch;
    const localIdRegex = /\[(\d+)\]/g;
    
    while ((idMatch = localIdRegex.exec(citationBlock)) !== null) {
      ids.push(parseInt(idMatch[1], 10));
    }
    
    // Create placeholders for all IDs found in this CITE tag
    return ids.map(id => `__CITATION_${id}__`).join('');
  });

  return {
    citations,
    processedContent
  };
}

/**
 * Creates a short preview of citation content for tooltips
 * @param content - Full citation content
 * @param maxLength - Maximum length for preview (default: 150)
 * @returns Shortened preview text
 */
export function createCitationPreview(content: string, maxLength: number = 150): string {
  if (content.length <= maxLength) {
    return content;
  }
  
  // Try to break at word boundary
  const truncated = content.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  
  if (lastSpaceIndex > maxLength * 0.8) {
    return truncated.substring(0, lastSpaceIndex) + '...';
  }
  
  return truncated + '...';
}
